// Renders lightweight guide content: ALL-CAPS section headers, "- " bullets,
// "- [ ]" checklists, "---" dividers, "label:" sub-labels, short Title-Case sub-headings,
// and inline [label](url) / bare-URL links.

const LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)|(https?:\/\/[^\s)]+)/g;

function renderInline(text, keyBase) {
  const out = [];
  let last = 0, m, k = 0;
  while ((m = LINK_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const href = m[2] || m[3];
    const label = m[1] || m[3];
    out.push(
      <a key={`${keyBase}-l${k++}`} href={href} target="_blank" rel="noreferrer"
         className="text-brand-terracotta font-medium underline underline-offset-2 hover:opacity-80 break-words">
        {label}
      </a>
    );
    last = LINK_RE.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const isDivider = (l) => l === "---";
const isHeading = (l) => {
  const letters = l.replace(/[^A-Za-z]/g, "");
  return letters.length >= 2 && l === l.toUpperCase() && !/^- /.test(l);
};
const checkRe = /^-\s\[( |x|X)\]\s?(.*)$/;
const isCheck = (l) => checkRe.test(l);
const isBullet = (l) => /^-\s+/.test(l) && !isCheck(l);
const isLabel = (l) => l.endsWith(":") && l.length <= 46 && !isBullet(l);
const isSubheading = (l) =>
  l.length <= 48 && !/[.!?:]$/.test(l) && !isHeading(l) && !isBullet(l) && !isDivider(l) && !LINK_RE.test(l);

export default function GuideContent({ text }) {
  const lines = (text || "").split("\n").map((l) => l.trim());
  const blocks = [];
  let bullets = null, checks = null;

  const flush = () => {
    if (bullets) { blocks.push({ type: "ul", items: bullets }); bullets = null; }
    if (checks) { blocks.push({ type: "checks", items: checks }); checks = null; }
  };

  lines.forEach((line) => {
    if (!line) { flush(); return; }
    LINK_RE.lastIndex = 0;
    if (isCheck(line)) { const mm = line.match(checkRe); (checks = checks || []).push(mm[2]); return; }
    if (isBullet(line)) { (bullets = bullets || []).push(line.replace(/^-\s+/, "")); return; }
    flush();
    if (isDivider(line)) blocks.push({ type: "hr" });
    else if (isHeading(line)) blocks.push({ type: "h2", text: line });
    else if (isLabel(line)) blocks.push({ type: "label", text: line });
    else if (isSubheading(line)) blocks.push({ type: "h3", text: line });
    else blocks.push({ type: "p", text: line });
  });
  flush();

  return (
    <div className="space-y-4">
      {blocks.map((b, i) => {
        if (b.type === "hr") return <hr key={i} className="border-brand-forest/10 my-6" />;
        if (b.type === "h2") return <h2 key={i} className="font-display text-xl sm:text-2xl text-brand-terracotta tracking-tight pt-2">{b.text}</h2>;
        if (b.type === "h3") return <h3 key={i} className="font-semibold text-brand-forest text-lg pt-1">{b.text}</h3>;
        if (b.type === "label") return <p key={i} className="font-semibold text-brand-forest">{b.text}</p>;
        if (b.type === "p") return <p key={i} className="text-brand-forest/80 leading-relaxed">{renderInline(b.text, i)}</p>;
        if (b.type === "ul") return (
          <ul key={i} className="space-y-1.5">
            {b.items.map((it, j) => (
              <li key={j} className="flex items-start gap-2.5 text-brand-forest/80">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand-terracotta shrink-0" />
                <span className="leading-relaxed">{renderInline(it, `${i}-${j}`)}</span>
              </li>
            ))}
          </ul>
        );
        if (b.type === "checks") return (
          <ul key={i} className="space-y-2">
            {b.items.map((it, j) => (
              <li key={j} className="flex items-start gap-2.5 text-brand-forest/80">
                <span className="mt-0.5 h-4 w-4 rounded border-2 border-brand-terracotta/60 shrink-0" />
                <span className="leading-relaxed">{renderInline(it, `${i}-${j}`)}</span>
              </li>
            ))}
          </ul>
        );
        return null;
      })}
    </div>
  );
}
